import clsx from 'clsx';
import { useTranslation } from 'next-i18next/pages';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import React, { useCallback, useState } from 'react';

import { LocationObject } from '@pflegia/types/common/location/location-object';
import Button from '@pflegia/ui/shared/button/Button.component';
import DropdownComponent, { DropdownVariant } from '@pflegia/ui/shared/form/dropdown/Dropdown.component';
import { PostalInputProps, PostalLocationInputVariant } from '@pflegia/ui/shared/form/postal-input/PostalInputTypes';
import FilterInputContainer from '@pflegia/ui/shared/job-board/FilterInputContainer';
import FilterInputLabel from '@pflegia/ui/shared/job-board/FilterInputLabel';
import Spinner from '@pflegia/ui/shared/spinner/Spinner';
import { removeCountryName } from '@pflegia/ui/shared/utils/remove-country-name';
import { buildLocationObject } from '@pflegia/util/common/location';
import { normalizeJobsParams } from '@pflegia/util/common/normalize-jobs-params';

import { Skeleton, SkeletonRow } from '@components/skeleton';

import { JOBS } from '@shared/application/constants';

import useJobTitlesFilterList, { JobTitleFilterValue } from 'domains/job-board/application/use-job-titles-filter-list.hook';

const PostalInputComponent = dynamic(() => import('@pflegia/ui/shared/form/postal-input/PostalInput'), {
  loading: () => (
    <Skeleton className='h-11 rounded-lg bg-gray-200'>
      <SkeletonRow />
    </Skeleton>
  ),
  ssr: false,
});

export const JOBS_SEARCH_ANCHOR_ID = 'jobs-hub-search';

interface JobsSearchCardProps {
  jobsCount?: number;
}

const JobsSearchCard: React.FC<JobsSearchCardProps> = ({ jobsCount }) => {
  const { t, i18n } = useTranslation('jobBoard', { keyPrefix: 'jobsPageHub.hero.searchCard' });
  const router = useRouter();
  const { jobTitles } = useJobTitlesFilterList();

  const [addressInput, setAddressInput] = useState('');
  const [location, setLocation] = useState<LocationObject | null>(null);
  const [jobTitle, setJobTitle] = useState<JobTitleFilterValue | null>(null);
  const [isNavigating, setIsNavigating] = useState(false);

  const handleAddressSelect = useCallback<NonNullable<PostalInputProps['onSelected']>>(address => {
    const locationObject = buildLocationObject(address);
    setLocation(locationObject);
    setAddressInput(removeCountryName(locationObject.formattedAddress));
  }, []);

  // The jobs page only resolves a city or a federal state from the location slug.
  const locationLabel = location?.city || location?.administrativeAreaLevel1;

  const handleSubmit = async () => {
    const segments = [normalizeJobsParams(jobTitle?.text), normalizeJobsParams(locationLabel)].filter(Boolean);
    setIsNavigating(true);
    try {
      await router.push([JOBS, ...segments].join('/'));
    } finally {
      setIsNavigating(false);
    }
  };

  const restChips = t('chips.rest', { returnObjects: true }) as string[];

  return (
    <div className='w-full scroll-mt-24 rounded-xl bg-white p-5 shadow-2xl' id={JOBS_SEARCH_ANCHOR_ID}>
      <div className='flex flex-col gap-4 md:flex-row md:items-end md:gap-6'>
        <FilterInputContainer>
          <FilterInputLabel>{t('filtersControl.location.label')}</FilterInputLabel>
          <PostalInputComponent
            dropdownHidden={Boolean(location)}
            id='jobs-hub-location'
            inputClassName='py-2.5'
            locationVariant={PostalLocationInputVariant.Postal}
            onChange={event => {
              setAddressInput(event.target.value);
            }}
            onFocus={() => {
              if (location) {
                setAddressInput('');
                setLocation(null);
              }
            }}
            onSelected={handleAddressSelect}
            placeholder={t('filtersControl.location.placeholder')}
            selectedAddresses={location ? [location] : []}
            showIcon={false}
            value={addressInput}
          />
        </FilterInputContainer>

        <FilterInputContainer>
          <FilterInputLabel>{t('filtersControl.position.label')}</FilterInputLabel>
          <DropdownComponent
            autocomplete
            classname='[&_input]:placeholder-default border-transparent'
            name='jobs-hub-position'
            onChange={option => setJobTitle(option)}
            onSearch={term => {
              if (term) setJobTitle(null);
            }}
            options={jobTitles}
            placeholder={t('filtersControl.position.placeholder')}
            size='sm'
            value={jobTitle}
            variant={DropdownVariant.Secondary}
          />
        </FilterInputContainer>

        <Button
          className={clsx('relative shrink-0', !jobTitle && !locationLabel ? 'cursor-not-allowed' : 'cursor-pointer')}
          disabled={(!jobTitle && !locationLabel) || isNavigating}
          onClick={handleSubmit}>
          <span className={clsx(isNavigating && 'invisible')}>{t('filtersControl.ctaLabel')}</span>
          {isNavigating && <Spinner className='h-6 w-6 text-white' containerClassName='absolute inset-0' />}
        </Button>
      </div>

      <div className='mt-4 flex flex-wrap justify-center gap-2 md:justify-start'>
        {jobsCount !== undefined && (
          <div className='border-primary-200 flex items-center gap-2 rounded-full border px-3 py-1'>
            <span className='relative flex h-2 w-2 shrink-0' aria-hidden>
              <span className='absolute inline-flex h-full w-full rounded-full bg-lime-500 opacity-75 motion-safe:animate-ping motion-reduce:animate-none' />
              <span className='relative inline-flex h-full w-full rounded-full bg-lime-500 shadow-[0_0_6px_rgba(34,197,94,0.7)]' />
            </span>
            <span className='text-secondary text-sm'>
              {t('chips.jobsNumber', { count: jobsCount.toLocaleString(i18n.language || 'de-DE') })}
            </span>
          </div>
        )}
        {restChips.map(chip => (
          <p className='border-primary-200 text-secondary gap-2 rounded-full border px-3 py-1 text-sm' key={chip}>
            {chip}
          </p>
        ))}
      </div>
    </div>
  );
};

export default JobsSearchCard;
